// AUTO-GENERATED — do not edit. Source: /shared/. Run scripts/sync-shared.sh to update.
/**
 * SLIK AUTHORITY — the ONE brain for "who can do what" (Jose 2026-10-25).
 * ======================================================================
 * The whack-a-mole fix for permissions: every surface (staff UI, calendar guards, checkout, services,
 * Cloud Functions) resolves authority THROUGH HERE instead of re-writing `role === 'owner' || 'manager'`
 * in a dozen places. Change the hierarchy once, everywhere follows.
 *
 * HIERARCHY (each level is a SUPERSET of the one below — owner encompasses manager + more):
 *     owner / admin  ⊇  manager  ⊇  senior  ⊇  barber
 * Owner ALWAYS wins: isOwner → every capability + every permission is true.
 *
 * CAPABILITIES are the operational "trust" grants the owner can hand an individual barber above their
 * base role (the per-barber config in the barber profile): override a double-book, check out late, edit
 * a past appointment, etc. `capabilities` on a barber record OVERRIDES the role default (grant OR deny).
 *
 * Pure logic + types only — NO React, NO Firebase (so it runs in the UI, the services, AND the functions).
 * Edit THIS file at repo root /shared and run scripts/sync-shared.sh. Do NOT edit the per-app copies.
 */

export type StaffRoleName =
  | 'owner' | 'manager' | 'senior' | 'barber' | 'trainee' | 'shop_attendant' | 'accountant' | 'customer';

/** Operational trust capabilities — grantable per-barber above their role default. */
export type Capability =
  | 'overrideConflict'       // book onto an occupied slot (the ⚡ squeeze / double-book)
  | 'lateCheckout'           // check out past the manager-required window
  | 'editPastAppointments'   // create/edit an appointment in the past (after the fact)
  | 'applyDiscount'          // apply a discount at checkout
  | 'voidTransaction'        // void / refund a sale
  | 'editSettings'           // shop settings
  | 'manageStaff';           // add/edit/remove barbers

export interface AuthorityInput {
  role?: string | null;
  accessLevel?: string | null;
  /** Per-barber trust grants that override the role default (true = grant, false = explicitly deny). */
  capabilities?: Partial<Record<Capability, boolean>> | null;
}

export interface Authority {
  role: StaffRoleName;
  isOwner: boolean;
  isManager: boolean;  // owner ⊇ manager → true for owners too
  isSenior: boolean;   // manager ⊇ senior
  isStaff: boolean;
  /** Does this operator have the capability? Owner → always true; else per-barber grant, else role default. */
  can: (cap: Capability) => boolean;
}

// Role DEFAULT capabilities (owner is handled by the isOwner short-circuit → gets everything).
const MANAGER_CAPS: Capability[] = ['overrideConflict', 'lateCheckout', 'editPastAppointments', 'applyDiscount', 'voidTransaction'];
const SENIOR_CAPS: Capability[] = ['applyDiscount'];
const BARBER_CAPS: Capability[] = [];

const norm = (s?: string | null) => String(s || '').toLowerCase().trim();

/** Owner/admin check (owner-identity) for callers that just need the boolean. */
export function isOwnerRole(role?: string | null, accessLevel?: string | null): boolean {
  const r = norm(role), a = norm(accessLevel);
  return r === 'owner' || r === 'admin' || a === 'admin';
}

/** Quick manager-or-owner check for non-React callers (services/guards) that just need the boolean. */
export function isManagerOrOwner(role?: string | null, accessLevel?: string | null): boolean {
  if (isOwnerRole(role, accessLevel)) return true;
  const r = norm(role), a = norm(accessLevel);
  return r === 'manager' || a === 'manager';
}

/** THE resolver. Feed it the acting operator's {role, accessLevel, capabilities}; get the authority. */
export function resolveAuthority(input: AuthorityInput): Authority {
  const r = norm(input.role);
  const a = norm(input.accessLevel);

  const isOwner = r === 'owner' || r === 'admin' || a === 'admin';
  const isManager = isOwner || r === 'manager' || a === 'manager';
  const isSenior = isManager || r === 'senior' || a === 'senior';

  const role: StaffRoleName = isOwner ? 'owner'
    : isManager ? 'manager'
    : isSenior ? 'senior'
    : (['barber', 'trainee', 'shop_attendant', 'accountant'].includes(r) ? (r as StaffRoleName) : 'barber');

  const isStaff = isSenior
    || ['barber', 'trainee', 'shop_attendant', 'accountant'].includes(r)
    || ['senior', 'barber'].includes(a);

  const base = new Set<Capability>(isManager ? MANAGER_CAPS : isSenior ? SENIOR_CAPS : BARBER_CAPS);
  const caps = input.capabilities || {};

  const can = (cap: Capability): boolean => {
    if (isOwner) return true;                       // owner ⊇ everything
    if (caps[cap] != null) return !!caps[cap];      // explicit per-barber grant/deny wins over the default
    return base.has(cap);                           // role default
  };

  return { role, isOwner, isManager, isSenior, isStaff, can };
}
